import React, { useRef } from 'react';
import { useQuote } from '../hooks/useData';
import { changeColor, fmtPct, fmtPrice } from './UI';

const MARKET_SHORT = {
  MIL: 'Milano', NYSE: 'NYSE', NASDAQ: 'NASDAQ',
  XETRA: 'XETRA', PARIS: 'Parigi', LSE: 'Londra', RAW: 'Altro',
};

function WatchRow({ item, onOpen, onRemove }) {
  const { data } = useQuote(item.ticker, item.market);
  const cur = data?.currency || 'EUR';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', borderBottom: '1px solid #161c28' }}>
      <button onClick={() => onOpen(item)} style={{
        flex: 1, display: 'flex', alignItems: 'center', gap: 12, background: 'transparent',
        border: 'none', cursor: 'pointer', textAlign: 'left', minWidth: 0,
      }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: '#e2e8f0', fontFamily: "'IBM Plex Mono', monospace" }}>{item.ticker}</span>
            <span style={{ fontSize: 12, color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 220 }}>{item.name}</span>
            <span style={{ fontSize: 10, color: '#475569', background: '#161c28', padding: '1px 7px', borderRadius: 20 }}>{MARKET_SHORT[item.market] || item.market}</span>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 13, color: '#e2e8f0', fontFamily: "'IBM Plex Mono', monospace" }}>
            {data?.price != null ? fmtPrice(data.price, cur) : '…'}
          </div>
          <div style={{ fontSize: 12, color: changeColor(data?.change_pct), fontFamily: "'IBM Plex Mono', monospace" }}>
            {data?.change_pct != null ? fmtPct(data.change_pct) : ''}
          </div>
        </div>
      </button>
      <button onClick={() => onRemove(item)} title="Rimuovi" style={{
        fontSize: 16, color: '#475569', background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px 8px',
      }}
        onMouseEnter={e => e.currentTarget.style.color = '#f87171'}
        onMouseLeave={e => e.currentTarget.style.color = '#475569'}>×</button>
    </div>
  );
}

function btn(bg, color, border) {
  return { fontSize: 12, padding: '6px 14px', borderRadius: 8, cursor: 'pointer',
           background: bg, color, border: `1px solid ${border}` };
}

export default function WatchlistPage({ watchlist, onOpen, onCompareAll }) {
  const { items, remove, importItems } = watchlist;
  const fileRef = useRef(null);

  const handleExport = () => {
    const blob = new Blob([JSON.stringify(items, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'watchlist.json'; a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const arr = JSON.parse(reader.result);
        if (Array.isArray(arr)) await importItems(arr);
      } catch { alert('File non valido'); }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: '#f1f5f9', marginBottom: 6 }}>La mia Watchlist</h1>
          <p style={{ fontSize: 13, color: '#64748b' }}>
            {items.length === 0 ? 'Vuota — aggiungi titoli con la ☆ dalla pagina di analisi.'
              : `${items.length} ${items.length === 1 ? 'titolo' : 'titoli'} salvati. Restano memorizzati anche dopo gli aggiornamenti.`}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {items.length >= 2 && (
            <button onClick={() => onCompareAll(items)} style={btn('#1e3a5f', '#60a5fa', '#2563eb40')}>⇄ Confronta tutti</button>
          )}
          <button onClick={handleExport} style={btn('transparent', '#94a3b8', '#1e2736')}>↓ Esporta</button>
          <button onClick={() => fileRef.current?.click()} style={btn('transparent', '#94a3b8', '#1e2736')}>↑ Importa</button>
          <input ref={fileRef} type="file" accept="application/json" onChange={handleImport} style={{ display: 'none' }} />
        </div>
      </div>

      {items.length === 0 ? (
        <div style={{ padding: '40px 20px', textAlign: 'center', color: '#475569', fontSize: 14, background: '#0d1117', border: '1px solid #1e2736', borderRadius: 12 }}>
          Nessun titolo nella watchlist.<br />
          <span style={{ fontSize: 13 }}>Apri un titolo e clicca la stella ☆ in alto per aggiungerlo qui.</span>
        </div>
      ) : (
        <div style={{ background: '#0d1117', border: '1px solid #1e2736', borderRadius: 12, overflow: 'hidden' }}>
          {items.map(item => (
            <WatchRow key={`${item.market}:${item.ticker}`} item={item} onOpen={onOpen}
              onRemove={(it) => remove(it.market, it.ticker)} />
          ))}
        </div>
      )}

      <div style={{ fontSize: 11, color: '#334155', marginTop: 12, lineHeight: 1.5 }}>
        La watchlist è salvata sul server dell'app e sopravvive a riavvii e aggiornamenti.
        Usa <strong style={{ color: '#475569' }}>Esporta</strong> per scaricarla come file e
        <strong style={{ color: '#475569' }}> Importa</strong> per ripristinarla su un altro computer.
      </div>
    </div>
  );
}
